'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Video, SubmitVideoForm, VIDEO_CATEGORIES, extractYouTubeId, extractVimeoId, getYouTubeThumbnail } from '@/data/videos';
import { createClient } from '@/lib/supabase/client';

interface SubmitVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (video: Omit<Video, 'id' | 'views' | 'comments' | 'status'>) => void;
}

function getYouTubeThumbnailOptions(videoId: string): { url: string; label: string }[] {
  return [
    { url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`, label: 'Max Quality' },
    { url: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`, label: 'Standard' },
    { url: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`, label: 'High Quality' },
    { url: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`, label: 'Mid Quality' },
  ];
}

function getVimeoThumbnailOptions(videoId: string): { url: string; label: string }[] {
  return [
    { url: `https://vumbnail.com/${videoId}.jpg`, label: 'Default' },
    { url: `https://vumbnail.com/${videoId}_large.jpg`, label: 'Large' },
    { url: `https://vumbnail.com/${videoId}_medium.jpg`, label: 'Medium' },
    { url: `https://vumbnail.com/${videoId}_small.jpg`, label: 'Small' },
  ];
}

/** Capture a JPEG blob from a video element at the given currentTime */
function captureFrameBlob(video: HTMLVideoElement, canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) { resolve(null); return; }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(resolve, 'image/jpeg', 0.85);
  });
}

/** Generate thumbnail data-URLs from a video File at 4 different timestamps */
async function generateVideoThumbnails(
  file: File,
  videoEl: HTMLVideoElement,
  canvasEl: HTMLCanvasElement
): Promise<{ url: string; label: string; blob: Blob }[]> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    videoEl.src = objectUrl;
    videoEl.muted = true;
    videoEl.preload = 'metadata';

    const results: { url: string; label: string; blob: Blob }[] = [];
    const labels = ['Opening', 'Early', 'Middle', 'Later'];

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      videoEl.src = '';
    };

    videoEl.addEventListener('error', () => {
      cleanup();
      reject(new Error('Failed to load video file'));
    }, { once: true });

    videoEl.addEventListener('loadedmetadata', async () => {
      const duration = videoEl.duration;
      if (!isFinite(duration) || duration <= 0) {
        cleanup();
        reject(new Error('Could not determine video duration'));
        return;
      }

      // Capture frames at 5%, 25%, 50%, 75% of duration
      const positions = [0.05, 0.25, 0.5, 0.75];

      for (let i = 0; i < positions.length; i++) {
        const seekTime = duration * positions[i];
        await new Promise<void>((seekResolve) => {
          const onSeeked = async () => {
            const blob = await captureFrameBlob(videoEl, canvasEl);
            if (blob) {
              const dataUrl = await new Promise<string>((r) => {
                const reader = new FileReader();
                reader.onload = () => r(reader.result as string);
                reader.readAsDataURL(blob);
              });
              results.push({ url: dataUrl, label: labels[i], blob });
            }
            seekResolve();
          };
          videoEl.addEventListener('seeked', onSeeked, { once: true });
          videoEl.currentTime = seekTime;
        });
      }

      cleanup();
      resolve(results);
    }, { once: true });

    videoEl.load();
  });
}

export default function SubmitVideoModal({ isOpen, onClose, onSubmit }: SubmitVideoModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [sourceType, setSourceType] = useState<'youtube' | 'vimeo' | 'upload'>('youtube');
  const [form, setForm] = useState<SubmitVideoForm>({
    title: '',
    tag: '',
    description: '',
    source: 'youtube',
    url: '',
    submittedBy: '',
    category: 'Other',
  });
  const [urlPreview, setUrlPreview] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [thumbnailOptions, setThumbnailOptions] = useState<{ url: string; label: string; blob?: Blob }[]>([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState<string>('');
  const [thumbnailLoadErrors, setThumbnailLoadErrors] = useState<Record<number, boolean>>({});

  // File upload state
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [generatingThumbs, setGeneratingThumbs] = useState(false);
  const [thumbGenError, setThumbGenError] = useState<string>('');
  const videoInputRef = useRef<HTMLInputElement>(null);
  const thumbGenVideoRef = useRef<HTMLVideoElement>(null);
  const thumbGenCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setForm({ title: '', tag: '', description: '', source: 'youtube', url: '', submittedBy: '', category: 'Other' });
      setUrlPreview(null);
      setUrlError('');
      setSubmitted(false);
      setSourceType('youtube');
      setThumbnailOptions([]);
      setSelectedThumbnail('');
      setThumbnailLoadErrors({});
      setVideoFile(null);
      setVideoPreviewUrl(null);
      setGeneratingThumbs(false);
      setThumbGenError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = isOpen ? 'hidden' : '';
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen]);

  // Revoke object URL when file changes or modal closes
  useEffect(() => {
    return () => {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    };
  }, [videoPreviewUrl]);

  const handleUrlChange = (url: string) => {
    setForm(prev => ({ ...prev, url }));
    setUrlError('');
    setUrlPreview(null);

    if (sourceType === 'youtube') {
      const id = extractYouTubeId(url);
      if (id) {
        setUrlPreview(getYouTubeThumbnail(id));
        setForm(prev => ({ ...prev, url, embedId: id } as any));
      } else if (url.length > 10) {
        setUrlError('Please enter a valid YouTube URL');
      }
    } else if (sourceType === 'vimeo') {
      const id = extractVimeoId(url);
      if (id) {
        setUrlPreview(`https://vumbnail.com/${id}.jpg`);
        setForm(prev => ({ ...prev, url, embedId: id } as any));
      } else if (url.length > 10) {
        setUrlError('Please enter a valid Vimeo URL');
      }
    }
  };

  const handleSourceChange = (src: 'youtube' | 'vimeo' | 'upload') => {
    setSourceType(src);
    setForm(prev => ({ ...prev, source: src === 'upload' ? 'upload' : src, url: '' }));
    setUrlPreview(null);
    setUrlError('');
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setThumbGenError('');
  };

  const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    const objUrl = URL.createObjectURL(file);
    setVideoFile(file);
    setVideoPreviewUrl(objUrl);
    setForm(prev => ({ ...prev, url: file.name, source: 'upload' }));
    setThumbGenError('');
  };

  const canProceedStep1 = () => {
    if (sourceType === 'upload') return !!videoFile;
    return form.url.length > 0 && !urlError;
  };

  const canSubmit = () => {
    return form.title.trim().length > 0 && form.submittedBy.trim().length > 0;
  };

  const handleProceedToThumbnails = async () => {
    if (sourceType === 'upload') {
      // Generate thumbnails from the video file
      if (!videoFile || !thumbGenVideoRef.current || !thumbGenCanvasRef.current) return;
      setGeneratingThumbs(true);
      setThumbGenError('');
      try {
        const frames = await generateVideoThumbnails(videoFile, thumbGenVideoRef.current, thumbGenCanvasRef.current);
        if (frames.length === 0) {
          setThumbGenError('Could not generate thumbnails from this video. Please try a different file.');
          setGeneratingThumbs(false);
          return;
        }
        setThumbnailOptions(frames);
        setSelectedThumbnail(frames[0].url);
        setThumbnailLoadErrors({});
        setStep(3);
      } catch (err: any) {
        setThumbGenError(err.message || 'Failed to generate thumbnails from video.');
      } finally {
        setGeneratingThumbs(false);
      }
      return;
    }

    const youtubeId = sourceType === 'youtube' ? extractYouTubeId(form.url) : undefined;
    const vimeoId = sourceType === 'vimeo' ? extractVimeoId(form.url) : undefined;

    let options: { url: string; label: string }[] = [];
    let defaultThumb = '';

    if (youtubeId) {
      options = getYouTubeThumbnailOptions(youtubeId);
      defaultThumb = options[0].url;
    } else if (vimeoId) {
      options = getVimeoThumbnailOptions(vimeoId);
      defaultThumb = options[0].url;
    }

    setThumbnailOptions(options);
    setSelectedThumbnail(defaultThumb);
    setThumbnailLoadErrors({});
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;

    setUploading(true);
    setUrlError('');

    try {
      const supabase = createClient();
      let thumbnail = selectedThumbnail || 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=640&h=360&fit=crop';
      let videoUrl = form.url;

      if (sourceType === 'upload' && videoFile) {
        // Upload video file to Supabase storage
        const videoFileName = `videos/${Date.now()}-${videoFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const { error: videoUploadErr, data: videoUploadData } = await supabase.storage
          .from('videos')
          .upload(videoFileName, videoFile, { upsert: false, contentType: videoFile.type });

        if (!videoUploadErr && videoUploadData) {
          const { data: videoUrlData } = supabase.storage.from('videos').getPublicUrl(videoFileName);
          videoUrl = videoUrlData.publicUrl;
        }

        // Upload the selected thumbnail blob to Supabase storage
        const selectedOption = thumbnailOptions.find(o => o.url === selectedThumbnail);
        if (selectedOption?.blob) {
          const thumbFileName = `thumbnails/${Date.now()}-thumb.jpg`;
          const { error: thumbUploadErr, data: thumbUploadData } = await supabase.storage
            .from('videos')
            .upload(thumbFileName, selectedOption.blob, { upsert: false, contentType: 'image/jpeg' });

          if (!thumbUploadErr && thumbUploadData) {
            const { data: thumbUrlData } = supabase.storage.from('videos').getPublicUrl(thumbFileName);
            thumbnail = thumbUrlData.publicUrl;
          } else {
            // Upload failed — use a safe fallback URL instead of the data: URL
            // (data: URLs are too large to store in the database and will appear broken)
            console.warn('Thumbnail upload failed:', thumbUploadErr?.message);
            thumbnail = 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=640&h=360&fit=crop';
          }
        } else {
          // No blob available — use fallback
          thumbnail = 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=640&h=360&fit=crop';
        }
      } else {
        const youtubeId = sourceType === 'youtube' ? extractYouTubeId(form.url) : undefined;
        if (!thumbnail && youtubeId) {
          thumbnail = getYouTubeThumbnail(youtubeId);
        }
      }

      const youtubeId = sourceType === 'youtube' ? extractYouTubeId(form.url) : undefined;
      const vimeoId = sourceType === 'vimeo' ? extractVimeoId(form.url) : undefined;

      const videoPayload = {
        title: form.title,
        tag: form.tag.toUpperCase() || 'SUBMITTED',
        description: form.description,
        source: sourceType === 'upload' ? 'upload' : sourceType,
        url: videoUrl,
        embed_id: youtubeId || vimeoId || null,
        thumbnail,
        thumbnail_alt: form.title,
        submitted_by: form.submittedBy,
        submitted_at: new Date().toISOString().split('T')[0],
        category: form.category,
        video_status: 'pending',
        views: 0,
        likes_count: 0,
      };

      try {
        const { data: insertedVideo } = await supabase.from('videos').insert(videoPayload).select('id, creator_id').single();

        if (insertedVideo?.creator_id) {
          supabase.functions.invoke('send-notification', {
            body: {
              type: 'new_video',
              payload: {
                creatorId: insertedVideo.creator_id,
                creatorName: form.submittedBy || 'A creator',
                videoTitle: form.title,
                videoId: insertedVideo.id,
              },
            },
          }).catch(() => {/* non-fatal */});
        }
      } catch {
        // Non-fatal
      }

      onSubmit({
        title: form.title,
        tag: form.tag.toUpperCase() || 'SUBMITTED',
        description: form.description,
        source: sourceType === 'upload' ? 'upload' : sourceType,
        url: videoUrl,
        embedId: youtubeId || vimeoId || undefined,
        thumbnail,
        thumbnailAlt: form.title,
        submittedBy: form.submittedBy,
        submittedAt: new Date().toISOString().split('T')[0],
        category: form.category,
      });
      setSubmitted(true);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  const ErrorBanner = ({ message }: { message: string }) => (
    <div className="flex items-start gap-2 bg-red-950 bg-opacity-60 border border-brand-red border-opacity-60 rounded-[4px] px-3 py-2.5 mt-2">
      <svg className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p className="text-brand-red text-sm leading-snug">{message}</p>
    </div>
  );

  return (
    <div className="modal-overlay animate-fade-in" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content animate-slide-up">
        {/* Hidden elements for thumbnail generation */}
        <video ref={thumbGenVideoRef} className="hidden" playsInline muted />
        <canvas ref={thumbGenCanvasRef} className="hidden" />

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-stroke-primary">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-brand-red"></div>
            <h2 className="text-xl font-bold text-text-primary uppercase tracking-wide">
              Submit Video
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors duration-150 p-1"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {submitted ? (
          /* Success State */
          <div className="px-4 sm:px-6 py-10 sm:py-12 text-center">
            <div className="w-16 h-16 bg-brand-red rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-text-primary mb-2">Video Submitted!</h3>
            <p className="text-text-secondary text-base mb-1">
              Your video is now <span className="text-yellow-400 font-bold">pending review</span>.
            </p>
            <p className="text-text-muted text-sm mb-6">
              Our team will review your submission and approve it if it meets our community guidelines.
              You&apos;ll typically see approved videos go live within 24 hours.
            </p>
            <button onClick={onClose} className="btn-primary">
              Got It
            </button>
          </div>
        ) : (
          <div className="px-4 sm:px-6 py-5">
            {/* Step Indicator — 3 steps */}
            <div className="flex items-center gap-1 mb-5 sm:mb-6">
              {/* Step 1 */}
              <div className={`flex items-center gap-1.5 text-xs font-bold uppercase ${
                step === 1 ? 'text-brand-red' : step > 1 ? 'text-text-secondary' : 'text-text-secondary'
              }`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  step > 1 ? 'bg-green-600 text-white' : step === 1 ? 'bg-brand-red text-white' : 'bg-stroke-primary text-text-secondary'
                }`}>
                  {step > 1 ? (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : '1'}
                </span>
                <span className="hidden sm:inline">Source</span>
              </div>
              <div className="flex-1 h-px bg-stroke-primary"></div>
              {/* Step 2 */}
              <div className={`flex items-center gap-1.5 text-xs font-bold uppercase ${
                step === 2 ? 'text-brand-red' : step > 2 ? 'text-text-secondary' : 'text-text-secondary'
              }`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  step > 2 ? 'bg-green-600 text-white' : step === 2 ? 'bg-brand-red text-white' : 'bg-stroke-primary text-text-secondary'
                }`}>
                  {step > 2 ? (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : '2'}
                </span>
                <span className="hidden sm:inline">Details</span>
              </div>
              <div className="flex-1 h-px bg-stroke-primary"></div>
              {/* Step 3 */}
              <div className={`flex items-center gap-1.5 text-xs font-bold uppercase ${
                step === 3 ? 'text-brand-red' : 'text-text-secondary'
              }`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  step === 3 ? 'bg-brand-red text-white' : 'bg-stroke-primary text-text-secondary'
                }`}>3</span>
                <span className="hidden sm:inline">Thumbnail</span>
              </div>
            </div>

            {/* ── STEP 1: Video Source ── */}
            {step === 1 && (
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-text-secondary text-sm uppercase tracking-wide mb-3">Video Source</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(['youtube', 'vimeo', 'upload'] as const).map((src) => (
                      <button
                        key={src}
                        onClick={() => handleSourceChange(src)}
                        className={`py-3 px-2 sm:px-4 rounded-[4px] border text-sm font-bold uppercase transition-all duration-150 ${
                          sourceType === src
                            ? 'border-brand-red bg-brand-red bg-opacity-10 text-brand-red' :'border-stroke-primary text-text-secondary hover:border-stroke-light hover:text-text-primary'
                        }`}
                      >
                        {src === 'youtube' && (
                          <span className="flex flex-col items-center gap-1">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                            YouTube
                          </span>
                        )}
                        {src === 'vimeo' && (
                          <span className="flex flex-col items-center gap-1">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197c1.185-1.044 2.351-2.084 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.48 4.807z"/>
                            </svg>
                            Vimeo
                          </span>
                        )}
                        {src === 'upload' && (
                          <span className="flex flex-col items-center gap-1">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Upload
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* YouTube / Vimeo URL input */}
                {(sourceType === 'youtube' || sourceType === 'vimeo') && (
                  <div>
                    <label className="block text-text-secondary text-sm uppercase tracking-wide mb-2">
                      {sourceType === 'youtube' ? 'YouTube URL' : 'Vimeo URL'}
                    </label>
                    <input
                      type="url"
                      value={form.url}
                      onChange={(e) => handleUrlChange(e.target.value)}
                      placeholder={sourceType === 'youtube' ? 'https://www.youtube.com/watch?v=...' : 'https://vimeo.com/...'}
                      className="input-field"
                    />
                    {urlError && <p className="text-brand-red text-sm mt-1">{urlError}</p>}
                    {urlPreview && (
                      <div className="mt-3 relative">
                        <img
                          src={urlPreview}
                          alt="Video thumbnail preview"
                          className="w-full h-40 object-cover rounded-[4px] border border-stroke-primary"
                        />
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-12 h-12 bg-brand-red bg-opacity-90 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}
                    {urlError && <ErrorBanner message={urlError} />}
                  </div>
                )}

                {/* Direct file upload */}
                {sourceType === 'upload' && (
                  <div>
                    <label className="block text-text-secondary text-sm uppercase tracking-wide mb-2">
                      Video File
                    </label>
                    <div
                      onClick={() => videoInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-[4px] p-5 text-center cursor-pointer transition-colors duration-150 ${
                        videoFile
                          ? 'border-brand-red bg-brand-red bg-opacity-5' :'border-stroke-primary hover:border-stroke-light'
                      }`}
                    >
                      {videoFile ? (
                        <div className="space-y-2">
                          {videoPreviewUrl && (
                            <video
                              src={videoPreviewUrl}
                              className="w-full h-36 object-cover rounded-[4px] mx-auto"
                              muted
                              playsInline
                            />
                          )}
                          <p className="text-text-primary text-sm font-semibold truncate">{videoFile.name}</p>
                          <p className="text-text-muted text-xs">{(videoFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setVideoFile(null);
                              setVideoPreviewUrl(null);
                              setForm(prev => ({ ...prev, url: '' }));
                              if (videoInputRef.current) videoInputRef.current.value = '';
                            }}
                            className="text-brand-red text-xs hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="py-3">
                          <svg className="w-8 h-8 text-text-muted mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <p className="text-text-secondary text-sm font-medium">Click to upload your video</p>
                          <p className="text-text-muted text-xs mt-1">MP4, WebM, MOV — up to 500MB</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/ogg"
                      onChange={handleVideoFileChange}
                      className="hidden"
                    />
                    <p className="text-text-muted text-xs mt-2">
                      Thumbnails will be auto-generated from your video in the next step.
                    </p>
                    {thumbGenError && <ErrorBanner message={thumbGenError} />}
                  </div>
                )}

                <button
                  onClick={() => setStep(2)}
                  disabled={!canProceedStep1()}
                  className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next: Add Details
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Dropbox / WeTransfer alternative — only shown for non-upload sources */}
                {sourceType !== 'upload' && (
                  <div className="border border-stroke-primary rounded-[4px] p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-5 bg-brand-red flex-shrink-0"></div>
                      <p className="text-text-primary text-sm font-bold uppercase tracking-wide">
                        Don&apos;t have a YouTube or Vimeo link?
                      </p>
                    </div>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      You can upload your video file directly to the platform via <span className="text-text-primary font-semibold">Dropbox</span> or <span className="text-text-primary font-semibold">WeTransfer</span> and send it to us — we&apos;ll handle the rest.
                    </p>
                    <ol className="space-y-2 text-sm text-text-secondary list-none">
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-brand-red bg-opacity-20 text-brand-red text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                        Upload your video to <span className="text-text-primary font-medium mx-1">Dropbox</span> or <span className="text-text-primary font-medium mx-1">WeTransfer</span>.
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-brand-red bg-opacity-20 text-brand-red text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                        Copy the share link from Dropbox or WeTransfer.
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-brand-red bg-opacity-20 text-brand-red text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                        Email the link along with your video title and description to:
                      </li>
                    </ol>
                    <a
                      href="mailto:viddstaratl@gmail.com"
                      className="flex items-center gap-2 bg-brand-red bg-opacity-10 border border-brand-red border-opacity-40 rounded-[4px] px-3 py-2.5 text-brand-red font-semibold text-sm hover:bg-opacity-20 transition-colors duration-150 w-fit"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      viddstaratl@gmail.com
                    </a>
                    <p className="text-text-muted text-xs">
                      Our team will upload and review your video within 24–48 hours.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 2: Video Details ── */}
            {step === 2 && (
              <div className="space-y-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-1 text-text-secondary hover:text-text-primary text-sm transition-colors duration-150 mb-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>

                <div>
                  <label className="block text-text-secondary text-sm uppercase tracking-wide mb-2">
                    Video Title <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="What happened in this video?"
                    maxLength={150}
                    className="input-field"
                  />
                  <p className="text-text-muted text-xs mt-1">{form.title.length}/150</p>
                </div>

                <div>
                  <label className="block text-text-secondary text-sm uppercase tracking-wide mb-2">
                    Short Tag <span className="text-text-muted text-xs normal-case">(optional — e.g. "WILD", "CAUGHT IN 4K")</span>
                  </label>
                  <input
                    type="text"
                    value={form.tag}
                    onChange={(e) => setForm(prev => ({ ...prev, tag: e.target.value.toUpperCase() }))}
                    placeholder="WILD"
                    maxLength={30}
                    className="input-field uppercase"
                  />
                </div>

                <div>
                  <label className="block text-text-secondary text-sm uppercase tracking-wide mb-2">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                    className="input-field"
                  >
                    {VIDEO_CATEGORIES.map(cat => (
                      <option key={cat} value={cat} className="bg-surface-tertiary">{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-text-secondary text-sm uppercase tracking-wide mb-2">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Give some context about what's happening in the video..."
                    rows={3}
                    maxLength={500}
                    className="input-field resize-none"
                  />
                </div>

                <div>
                  <label className="block text-text-secondary text-sm uppercase tracking-wide mb-2">
                    Your Username <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.submittedBy}
                    onChange={(e) => setForm(prev => ({ ...prev, submittedBy: e.target.value }))}
                    placeholder="YourUsername"
                    maxLength={50}
                    className="input-field"
                  />
                </div>

                <div className="bg-surface-tertiary border border-stroke-secondary rounded-[4px] p-4">
                  <p className="text-text-muted text-sm">
                    <span className="text-yellow-400 font-bold">⚠ Review Required:</span> All submitted videos are reviewed by our moderation team before going live. Submissions that violate community guidelines will be rejected.
                  </p>
                </div>

                <button
                  onClick={handleProceedToThumbnails}
                  disabled={!canSubmit() || generatingThumbs}
                  className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {generatingThumbs ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Generating Thumbnails…
                    </>
                  ) : (
                    <>
                      Next: Choose Thumbnail
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
                {thumbGenError && <ErrorBanner message={thumbGenError} />}
              </div>
            )}

            {/* ── STEP 3: Thumbnail Selection ── */}
            {step === 3 && (
              <div className="space-y-4">
                <button
                  onClick={() => setStep(2)}
                  className="flex items-center gap-1 text-text-secondary hover:text-text-primary text-sm transition-colors duration-150"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>

                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-1 h-5 bg-brand-red flex-shrink-0"></div>
                    <h3 className="text-text-primary text-sm font-bold uppercase tracking-wide">Choose Your Thumbnail</h3>
                  </div>
                  <p className="text-text-muted text-xs mb-4">
                    {sourceType === 'upload' ?'These thumbnails were auto-generated from your video. Select the frame that best represents it.' :'Select the thumbnail that best represents your video. This is what viewers will see before clicking.'}
                  </p>

                  {sourceType === 'upload' && thumbnailOptions.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-8 gap-2 text-text-muted">
                      <div className="w-6 h-6 border-2 border-text-muted border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-sm">Generating thumbnails…</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {thumbnailOptions.map((option, index) => {
                      const isSelected = selectedThumbnail === option.url;
                      const hasError = thumbnailLoadErrors[index];
                      return (
                        <button
                          key={index}
                          onClick={() => setSelectedThumbnail(option.url)}
                          className={`relative rounded-[4px] overflow-hidden border-2 transition-all duration-150 focus:outline-none ${
                            isSelected
                              ? 'border-brand-red ring-2 ring-brand-red ring-opacity-40' :'border-stroke-primary hover:border-stroke-light'
                          }`}
                        >
                          {hasError ? (
                            <div className="w-full h-28 bg-surface-tertiary flex flex-col items-center justify-center gap-1">
                              <svg className="w-6 h-6 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-text-muted text-xs">Not available</span>
                            </div>
                          ) : (
                            <img
                              src={option.url}
                              alt={`Thumbnail option ${index + 1}: ${option.label}`}
                              className="w-full h-28 object-cover"
                              onError={() => setThumbnailLoadErrors(prev => ({ ...prev, [index]: true }))}
                            />
                          )}
                          {/* Label bar */}
                          <div className={`px-2 py-1 text-xs font-semibold text-center transition-colors duration-150 ${
                            isSelected ? 'bg-brand-red text-white' : 'bg-surface-secondary text-text-secondary'
                          }`}>
                            {option.label}
                          </div>
                          {/* Selected checkmark */}
                          {isSelected && (
                            <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-brand-red rounded-full flex items-center justify-center shadow">
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Selected thumbnail preview */}
                  {selectedThumbnail && (
                    <div className="mt-4">
                      <p className="text-text-muted text-xs uppercase tracking-wide mb-2">Selected Thumbnail Preview</p>
                      <div className="relative rounded-[4px] overflow-hidden border border-brand-red border-opacity-50">
                        <img
                          src={selectedThumbnail}
                          alt="Selected thumbnail preview"
                          className="w-full h-40 object-cover"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent px-3 py-2">
                          <p className="text-white text-xs font-semibold truncate">{form.title || 'Your Video Title'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!selectedThumbnail || uploading}
                  className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {sourceType === 'upload' ? 'Uploading…' : 'Submitting…'}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Submit for Review
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
